import { useState } from "react";
import { Eye, EyeOff, Lock, Mail, ArrowLeft } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { ApiError } from "../../lib/api";
import Logo from "../../assets/images/logo-removebg.png";

interface FormErrors {
  email?: string;
  password?: string;
}

interface LoginPageProps {
  onBackClick?: () => void;
  onLoginSuccess?: () => void;
  onRegisterClick?: () => void;
  onForgotPasswordClick?: () => void;
}

export default function LoginPage({
  onBackClick,
  onLoginSuccess,
  onRegisterClick,
  onForgotPasswordClick,
}: LoginPageProps) {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Uses authentication context
  const { login } = useAuth();

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!email) {
      newErrors.email = "Email é obrigatório";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email inválido";
    }

    if (!password) {
      newErrors.password = "Senha é obrigatória";
    } else if (password.length < 6) {
      newErrors.password = "Senha deve ter no mínimo 6 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validateForm()) {
      setIsLoading(true);

      try {
        // Uses login function from context
        const success = await login(email, password, rememberMe);

        if (success && onLoginSuccess) {
          onLoginSuccess();
        }
      } catch (error) {
        console.error("Erro no login:", error);
        if (error instanceof ApiError) {
          setErrors({ password: error.message });
        } else if (error instanceof Error) {
          setErrors({ password: error.message });
        } else {
          setErrors({ password: "Não foi possível realizar o login" });
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-(--bg-secondary) flex items-center justify-center p-4">
      {/* Back Button */}
      {onBackClick && (
        <button
          onClick={onBackClick}
          className="
            fixed top-6 left-6
            flex items-center gap-2
            px-4 py-2
            rounded-lg
            text-sm font-medium
            text-(--text-tertiary)
            transition-all duration-200
          hover:text-(--text-primary)
          hover:bg-(--bg-accent)
          z-10
          "
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar</span>
        </button>
      )}

      {/* Responsive Container */}
      <div className="w-full max-w-md lg:max-w-4xl">
        <div
          className="bg-(--card-bg) rounded-2xl shadow-lg border lg:flex lg:overflow-hidden"
          style={{ borderColor: "var(--border-color)" }}
        >
          {/* Logo Section - Only on large screens */}
          <div
            className="hidden lg:flex lg:w-5/12 lg:flex-col lg:items-center lg:justify-center lg:p-12"
            style={{ backgroundColor: "var(--bg-secondary)" }}
          >
            <img src={Logo} alt="Logo" className="w-56 h-56 rounded-full" />
            <p
              className="text-center text-base"
              style={{ color: "var(--text-tertiary)" }}
            >
              Acesse sua conta de forma segura
            </p>
          </div>

          {/* Form Section */}
          <div className="p-8 lg:w-7/12 lg:p-12">
            {/* Header - Mobile */}
            <div className="text-center mb-8 lg:text-left">
              <div className="lg:hidden inline-flex items-center justify-center">
                <img src={Logo} alt="Logo" className="w-25 h-25 rounded-full" />
              </div>
              <h1
                className="text-3xl font-bold mb-2"
                style={{ color: "var(--text-primary)" }}
              >
                Bem-vindo
              </h1>
              <p
                className="text-base"
                style={{ color: "var(--text-tertiary)" }}
              >
                Entre na sua conta para continuar
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-2"
                  style={{ color: "var(--text-primary)" }}
                >
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail
                      className="h-5 w-5"
                      style={{ color: "var(--text-tertiary)" }}
                    />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    className="block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 transition disabled:opacity-50 placeholder-(--text-tertiary)"
                    style={{
                      borderColor: errors.email
                        ? "var(--danger-color)"
                        : "var(--border-color)",
                      color: "var(--text-primary)",
                      backgroundColor: "var(--input-bg)",
                    }}
                    placeholder="seu@email.com"
                  />
                </div>
                {errors.email && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: "var(--danger-color)" }}
                  >
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password Input */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium mb-2"
                  style={{ color: "var(--text-primary)" }}
                >
                  Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock
                      className="h-5 w-5"
                      style={{ color: "var(--text-tertiary)" }}
                    />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    className="block w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 transition disabled:opacity-50 placeholder-(--text-tertiary)"
                    style={{
                      borderColor: errors.password
                        ? "var(--danger-color)"
                        : "var(--border-color)",
                      color: "var(--text-primary)",
                      backgroundColor: "var(--input-bg)",
                    }}
                    placeholder="Digite sua senha"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff
                        className="h-5 w-5 hover:opacity-75 transition"
                        style={{ color: "var(--text-tertiary)" }}
                      />
                    ) : (
                      <Eye
                        className="h-5 w-5 hover:opacity-75 transition"
                        style={{ color: "var(--text-tertiary)" }}
                      />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: "var(--danger-color)" }}
                  >
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading}
                    className="h-4 w-4 border-gray-300 rounded"
                    style={{ accentColor: "var(--primary-color)" }}
                  />
                  <span
                    className="ml-2 text-sm"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    Manter conectado
                  </span>
                </label>
                <button
                  onClick={onForgotPasswordClick}
                  disabled={isLoading}
                  className="text-sm font-medium hover:opacity-75 transition"
                  style={{ color: "var(--primary-color)" }}
                >
                  Esqueceu a senha?
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full text-white py-3 px-4 rounded-lg font-semibold hover:shadow-lg focus:outline-none focus:ring-2 transition-all shadow-md disabled:opacity-50"
                style={{ backgroundColor: "var(--primary-color)" }}
              >
                {isLoading ? "Entrando..." : "Entrar"}
              </button>
            </form>

            {/* Sign Up Link */}
            <p
              className="mt-6 text-center lg:text-left text-sm"
              style={{ color: "var(--text-tertiary)" }}
            >
              Não tem uma conta?{" "}
              <button
                onClick={onRegisterClick}
                disabled={isLoading}
                className="font-medium hover:opacity-75 transition"
                style={{ color: "var(--primary-color)" }}
              >
                Cadastre-se
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
